import { Suspense, useContext, useEffect } from 'react';
import PT from 'prop-types';
import { Divider, Drawer, Grid2, Toolbar } from '@mui/material';
import { useMediaQuery } from '@mui/system';
import NavigationHeader from 'components/cards/navigation-header';
import { AssetContext, DataTemplatesProvider } from 'context';
import { FoldingDrawer } from 'components/drawer';
import PageLayoutBox from 'components/layout/page-layout-styles';
import Sidebar from 'components/sidebar';
import ExpandButton from 'components/drawer/permanent/expand-button';
import AssetWizard from '../asset-wizard';
import { StyledBox } from '../parameters-page-styles';
import Assets from './assets';
import useAssetNavigation from './hook/use-asset-navigation';
import Filter from './filter';

const AssetNavigation = ({
  panelRef,
  isPanelExpanded,
  setIsPanelExpanded,
  panelSize,
}) => {
  const { wizardOpen } = useContext(AssetContext);
  const {
    assetNodes,
    loading,
    isProjectAssetHierarchy,
    filterMenu,
    handleOpenCloseWizard,
    expanded,
    setExpanded,
  } = useAssetNavigation();
  const screenXs = useMediaQuery(theme => theme.breakpoints.down('sm'));

  useEffect(() => {
    if (panelRef?.current) {
      setIsPanelExpanded(panelRef?.current?.isExpanded());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [panelRef]);

  const togglePanel = () => {
    if (panelRef?.current?.isExpanded()) {
      panelRef?.current?.collapse();
      setIsPanelExpanded(false);
    } else {
      panelRef?.current?.expand();
      setIsPanelExpanded(true);
    }
  };

  const contents = (
    <>
      <Suspense fallback={null}>
        {screenXs && <ExpandButton open onClick={togglePanel} />}
        <NavigationHeader
          open={isPanelExpanded}
          handleClick={handleOpenCloseWizard}
          label="parameters"
          hasProjectCard
        />
      </Suspense>
      <Divider />
      {isPanelExpanded && (
        <>
          <Grid2 container>
            <Grid2
              item
              size={{
                xs: 12,
                sm: 12,
                md: 12,
                lg: 12,
              }}
            >
              <Filter
                menu={filterMenu}
                setExpanded={setExpanded}
                expanded={expanded}
              />
            </Grid2>
          </Grid2>
          <Assets
            loading={loading}
            assetNodes={assetNodes}
            isProjectAssetHierarchy={isProjectAssetHierarchy}
            expanded={expanded}
            setExpanded={setExpanded}
            panelSize={panelSize}
          />
        </>
      )}
    </>
  );

  return (
    <>
      {screenXs && isPanelExpanded ? (
        <Drawer open>
          <Toolbar />
          <div
            style={{
              display: 'flex',
              overflow: 'hidden',
              width: '100%',
              height: `calc(100vh - 60px)`,
            }}
          >
            <Sidebar />
            <div style={{ height: `calc(100% - 200px)` }}>{contents}</div>
          </div>
        </Drawer>
      ) : (
        <FoldingDrawer
          open={isPanelExpanded}
          onClick={togglePanel}
          collapsedTooltip="Asset Navigation"
        >
          <PageLayoutBox>
            <StyledBox
              sx={{ overflowY: 'hidden' }}
              aria-label="assetNavigation"
            >
              {contents}
            </StyledBox>
          </PageLayoutBox>
        </FoldingDrawer>
      )}
      {wizardOpen && (
        <Suspense fallback={null}>
          <DataTemplatesProvider>
            <AssetWizard open={wizardOpen} toggleOpen={handleOpenCloseWizard} />
          </DataTemplatesProvider>
        </Suspense>
      )}
    </>
  );
};

AssetNavigation.propTypes = {
  panelRef: PT.shape({
    current: PT.shape({
      isExpanded: PT.func,
      expand: PT.func,
      collapse: PT.func,
    }),
  }),
  isPanelExpanded: PT.bool,
  setIsPanelExpanded: PT.func,
  panelSize: PT.number,
};
AssetNavigation.defaultProps = {
  panelRef: null,
  isPanelExpanded: false,
  setIsPanelExpanded: () => {},
};

export default AssetNavigation;
